import { useStore } from '@nanostores/react';
import { useSearchParams } from '@remix-run/react';
import { motion, type Variants } from 'framer-motion';
import { memo, useCallback, useEffect, useRef, useState, type SyntheticEvent } from 'react';
import { toast } from 'react-toastify';
import { ChatList } from './ChatList';
import { MenuItems } from './MenuItems';
import { Dialogs } from '~/components/ui/Dialogs.client';
import { Logo } from '~/components/ui/Logo';
import { UserAvatar } from '~/components/ui/UserAvatar';
import { acceptInvitation } from '~/lib/.client/api/organizations';
import { useClickOutsideToCloseMenu } from '~/lib/hooks/useClickOutsideToCloseMenu';
import { chatStore } from '~/lib/stores/chat';
import { menuStore, SettingsPage } from '~/lib/stores/menu';
import {
  INVITE_TO_TEAM_SEARCH_PARAM,
  INVITE_TOKEN_SEARCH_PARAM,
  SHOW_TEAM_SETTINGS_PARAM,
} from '~/lib/stores/pricing/config';
import { userStore } from '~/lib/stores/user';
import { versionHistoryStore } from '~/lib/stores/version-history/version-history';
import type { UserData } from '~/types/user';
import { classNames } from '~/utils/classNames';
import { cubicEasingFn } from '~/utils/easings';
import { isTouchDevice } from '~/utils/isTouchDevice';
import { renderLogger } from '~/utils/logger';

const ASIDE_MENU_ID = 'aside-menu';

const menuVariants = {
  closed: {
    opacity: 0,
    visibility: 'hidden',
    left: '-150px',
    transition: {
      duration: 0.2,
      ease: cubicEasingFn,
    },
  },
  open: {
    opacity: 1,
    visibility: 'initial',
    left: 0,
    transition: {
      duration: 0.2,
      ease: cubicEasingFn,
    },
  },
} satisfies Variants;

interface MenuProps {
  user: UserData;
}

export const Menu = memo(({ user }: MenuProps) => {
  renderLogger.trace('Menu');

  const isVersionHistoryOpen = useStore(versionHistoryStore.isOpen);

  const menuRef = useRef<HTMLDivElement>(null);

  const activeOrganization = useStore(userStore.getActiveOrganization());
  const chatStarted = useStore(chatStore.started);

  const showAccountSelector = useStore(userStore.getShowAccountSelector());

  const [open, $setOpen] = useState(false);
  const [isAccountSelectorMenuVisible, setIsAccountSelectorMenuVisible] = useState(false);
  const [searchParams] = useSearchParams();

  const setOpen = useCallback(
    (open: boolean) => {
      if (isVersionHistoryOpen) {
        $setOpen(open ? false : open);
      } else {
        $setOpen(open);
      }
    },
    [isVersionHistoryOpen],
  );

  useEffect(() => {
    if (isVersionHistoryOpen) {
      $setOpen(false);
    }
  }, [isVersionHistoryOpen]);

  useEffect(() => {
    if (searchParams.get(SHOW_TEAM_SETTINGS_PARAM)) {
      const activeOrganization = user?.organizations?.find(
        (org) => org.slug === searchParams.get(SHOW_TEAM_SETTINGS_PARAM),
      );

      if (activeOrganization) {
        userStore.setActiveOrganization(activeOrganization);
        setOpen(true);
        menuStore.openDialog({ type: 'settings', initialPage: SettingsPage.Team });
      }
    }

    const inviteToken = searchParams.get(INVITE_TOKEN_SEARCH_PARAM);
    const inviteToTeam = searchParams.get(INVITE_TO_TEAM_SEARCH_PARAM);

    if (inviteToken && inviteToTeam) {
      acceptInvitation({ token: inviteToken })
        .then(() => {
          toast.success(`You have successfully joined the team ${inviteToTeam}`);
          setTimeout(() => {
            const url = new URL(window.location.href);
            url.searchParams.delete(INVITE_TOKEN_SEARCH_PARAM);
            url.searchParams.delete(INVITE_TO_TEAM_SEARCH_PARAM);
            url.searchParams.set(SHOW_TEAM_SETTINGS_PARAM, inviteToTeam);
            window.open(url, '_self');
          }, 750);
        })
        .catch(() => {
          toast.error(`Could not join the team ${inviteToTeam}. Make sure that the invitation is valid.`);
        });
    }
  }, [searchParams]);

  const loadChatList = useCallback(async () => {
    try {
      await chatStore.loadChatList();
    } catch {
      toast.error('Failed to load your chats');
    }
  }, []);

  const showMenu = useCallback(() => {
    setOpen(true);
  }, []);

  const closeMenu = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    loadChatList();
  }, []);

  useEffect(() => {
    const enterThresholdX = 15;
    const exitThreshold = 40;

    function onMouseMove(event: MouseEvent) {
      if (chatStore.showChat.get() && event.pageX < enterThresholdX) {
        setOpen(true);
      }

      if (
        !isAccountSelectorMenuVisible &&
        menuRef.current &&
        event.clientX > menuRef.current.getBoundingClientRect().right + exitThreshold
      ) {
        setOpen(false);
      }
    }

    window.addEventListener('mousemove', onMouseMove);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
    };
  }, [isAccountSelectorMenuVisible, setOpen]);

  /** Close the dropdown when clicking outside of it on touch devices */
  useClickOutsideToCloseMenu({
    elementId: ASIDE_MENU_ID,
    condition: !open || !isTouchDevice || isAccountSelectorMenuVisible,
    onClose: closeMenu,
  });

  const onToggleAccountSelector = useCallback(() => {
    setIsAccountSelectorMenuVisible((value) => !value);
  }, []);

  const handleCloseMenuOnMobile = useCallback(
    (event: SyntheticEvent<HTMLElement>) => {
      const target = event.target as HTMLElement;
      const isInteractiveTarget = target && ['a', 'button'].includes(target.nodeName.toLowerCase());

      if (isTouchDevice && isInteractiveTarget) {
        closeMenu();
      }
    },
    [closeMenu],
  );

  return (
    <>
      <div
        className={classNames('flex flex-col items-center justify-end pl-2.5 py-3', {
          'fixed top-[var(--header-height)] bottom-0 z-20': !chatStarted,
        })}
      >
        <div className="ml-px flex items-center">
          <button
            className="bg-transparent text-bolt-elements-textSecondary cursor-none flex flex-col items-center"
            onMouseOver={showMenu}
            onClick={showMenu}
          >
            <UserAvatar className="w-6 h-6 mb-2" avatar={activeOrganization?.avatar ?? user.avatar} />
            <div className="i-ph:sidebar-simple-duotone text-xl" />
          </button>
        </div>
      </div>

      <motion.div
        id={ASIDE_MENU_ID}
        ref={menuRef}
        initial="closed"
        animate={open ? 'open' : 'closed'}
        variants={menuVariants}
        className="flex selection-accent flex-col side-menu fixed top-0 w-[350px] h-full bg-bolt-elements-background-depth-2 border-r rounded-r-3xl border-bolt-elements-borderColor z-sidebar shadow-xl shadow-bolt-elements-sidebar-dropdownShadow text-sm"
        onClick={handleCloseMenuOnMobile}
      >
        <div className="flex items-center h-[var(--header-height)] px-4 ">
          {/* Placeholder */}
          {!chatStarted && <Logo />}
          {isTouchDevice && (
            <button
              className="ml-auto flex items-center justify-center p-2.5 relative z-context-menu rounded-md focus:outline-none bg-bolt-ds-surface-highlight"
              aria-label="Close menu"
              onClick={closeMenu}
            >
              <span className="i-ph:x text-base" />
            </button>
          )}
        </div>
        <div className="flex-1 flex flex-col h-full w-full overflow-hidden">
          <ChatList />
          <MenuItems
            user={user}
            isAccountSelectorMenuVisible={isAccountSelectorMenuVisible}
            onToggleAccountSelector={onToggleAccountSelector}
            activeOrganization={activeOrganization}
            showAccountSelector={showAccountSelector}
          />
        </div>
      </motion.div>

      <Dialogs />
    </>
  );
});
